export const PLAYER_UI_SETTINGS_STORAGE_KEY = "player:ui-settings:v1";

type ThemeMode = "system" | "light" | "dark";

export interface PlayerUiSettings {
  showFurigana: boolean;
  showRomaji: boolean;
  showChinese: boolean;
  hideUnderline: boolean;
  hideGrammarBackground: boolean;
  hideShadowingSpotlight: boolean;
  showOnlyChinese: boolean;
  highlightNewWords: boolean;
  fontSize: number;
  blurStrength: number;
  volume: number;
  theme: ThemeMode;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function asTheme(value: unknown): ThemeMode | undefined {
  return value === "system" || value === "light" || value === "dark" ? value : undefined;
}

export function readPlayerUiSettings(): Partial<PlayerUiSettings> {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage.getItem(PLAYER_UI_SETTINGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;

    const fontSizeRaw = asNumber(parsed.fontSize);
    const blurRaw = asNumber(parsed.blurStrength);
    const volumeRaw = asNumber(parsed.volume);

    return {
      showFurigana: asBoolean(parsed.showFurigana),
      showRomaji: asBoolean(parsed.showRomaji),
      showChinese: asBoolean(parsed.showChinese),
      hideUnderline: asBoolean(parsed.hideUnderline),
      hideGrammarBackground: asBoolean(parsed.hideGrammarBackground),
      hideShadowingSpotlight: asBoolean(parsed.hideShadowingSpotlight),
      showOnlyChinese: asBoolean(parsed.showOnlyChinese),
      highlightNewWords: asBoolean(parsed.highlightNewWords),
      fontSize: fontSizeRaw == null ? undefined : clamp(fontSizeRaw, 0.8, 2),
      blurStrength: blurRaw == null ? undefined : clamp(blurRaw, 0, 10),
      volume: volumeRaw == null ? undefined : clamp(volumeRaw, 0, 1),
      theme: asTheme(parsed.theme),
    };
  } catch {
    return {};
  }
}

export function writePlayerUiSettings(settings: PlayerUiSettings) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(PLAYER_UI_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore storage write errors on private browsing/device restrictions.
  }
}
